import { forwardRef } from 'react';

import cn from 'classnames';

import type { BaseComponentProps } from '../../../../types/common';
import { MarginProp, TextAlignProp } from '../../../../types/cssProperties';
import type {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
} from '../../../../types/polymorphic';

import styles from './Text.module.css';
import type { TextTags, TextColorProp } from './types';

const DEFAULT_TEXT_ELEMENT: TextTags = 'p';

// The exclusive props of the `Text` component
export interface TextOwnProps extends BaseComponentProps {
  /**
   * It shows an ellipsis when the text overflows outside its container
   */
  textOverflowEllipsis?: boolean;
  // It defines how the text will look
  variant?:
    | 'h1plus-large'
    | 'h1plus-small'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'h6plus'
    | 'h7'
    | 'p0'
    | 'p1'
    | 'p2'
    | 'p3'
    | 'p4';
  /**
   * It defines the margin of the component. It accepts the same values as the CSS margin property
   */
  margin?: MarginProp;
  /**
   * It defines the text alignment of the component. It accepts the same values as the CSS text-align property
   */
  textAlign?: TextAlignProp;
  /**
   * It defines the color of the component. It uses the names of our color tokens
   */
  color?: TextColorProp;
  /**
   * It defines if the text should be bold or not
   */
  bold?: boolean;
}

// The props for the `Text` component, including the `as` and `children` props
export type TextProps<C extends React.ElementType> =
  PolymorphicComponentPropWithRef<C, TextOwnProps>;

/**
 * Base component that manages all typography styles depending on the `variant` prop.
 * It's a polymorphic component that can be rendered as any HTML tag.
 *
 * DO NOT use it in your features directly. Use the specific components like `P1`, `H1`, etc.
 */
export const Text = forwardRef(
  <C extends React.ElementType = typeof DEFAULT_TEXT_ELEMENT>(
    {
      as,
      children,
      variant = 'p1',
      textOverflowEllipsis = false,
      dataTestId,
      margin,
      textAlign,
      color,
      bold,
    }: TextProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const Component = as || DEFAULT_TEXT_ELEMENT;

    const style = {
      margin,
      textAlign,
    };

    return (
      <Component
        ref={ref}
        className={cn(styles[variant], color && styles[`color-${color}`], {
          [styles['text-overflow-ellipsis']]: textOverflowEllipsis,
        })}
        data-testid={dataTestId}
        style={style}
      >
        {bold ? <b>{children}</b> : children}
      </Component>
    );
  }
);
Text.displayName = 'Text';
